import { useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import { useSession } from '@clerk/clerk-react';

/* ONE Supabase client for the whole app, with the Clerk token injected per
 * request.
 *
 * WHAT WAS WRONG
 * `useSupabase()` used to call `createClient()` on every invocation, and every
 * caller calls it before every query — settings, messages, outputs,
 * niche_versions, testimonials, offers, social_posts. Each client builds its own
 * GoTrueClient, so one page load produced six or more, and Supabase warned once
 * per instance:
 *
 *   "Multiple GoTrueClient instances detected in the same browser context."
 *
 * Noise rather than damage (auth is Clerk, so there's no Supabase session to
 * corrupt) but it buried real errors — a `Failed to save social_posts` scrolled
 * past in the middle of it and cost real debugging time.
 *
 * WHY NOT JUST CACHE THE CLIENT PER TOKEN
 * That was tried first and it isn't enough. Clerk rotates its JWT about every
 * minute, so a token-keyed cache has to build a new client on every rotation —
 * and the warning fires per instance, so it would come back roughly once a minute
 * for the life of the session. A plain singleton is worse: it would pin the FIRST
 * token into the Authorization header forever and every query would 401 once that
 * token expired, in production, long after localhost looked fine.
 *
 * THE FIX
 * Build the client exactly once and give it a `fetch` that reads the current token
 * at request time. One GoTrueClient ever, so zero warnings; and the token is never
 * stale, because it's fetched per request rather than baked into a header at
 * construction. This is Supabase's recommended shape for third-party auth.
 */

let client = null;

/* Set by `useSupabase()` on every render so it always points at the live Clerk
 * session. Module-level because the single client outlives any one component, and
 * there is only ever one signed-in user in this app.
 *
 * Returning null (rather than throwing) when unset matters: a request that fires
 * before sign-in completes should go out unauthenticated and be rejected by RLS,
 * not blow up in the fetch layer.
 */
let getToken = async () => null;

/**
 * The single client. Exported so it can be tested without mounting a component —
 * the app reaches it through `useSupabase()`, which also registers the token
 * getter. Calling this directly without a registered getter sends no
 * Authorization header.
 */
export function getSupabaseClient() {
  if (client) return client;
  client = createClient(
    // Read lazily rather than at module scope so tests can set these. CRA inlines
    // REACT_APP_* wherever it appears, so this is identical in a build.
    process.env.REACT_APP_SUPABASE_URL,
    process.env.REACT_APP_SUPABASE_ANON_KEY,
    {
      /* Nothing in the app calls `sb.auth`, subscribes to realtime, or uses
         Supabase storage — every request is authorised by the header below. So
         session persistence and refresh are pure overhead. */
      auth: { persistSession: false, autoRefreshToken: false },
      global: {
        fetch: async (input, init = {}) => {
          const token = await getToken();
          /* `new Headers(...)` rather than an object spread, and this is
             load-bearing: supabase-js passes `init.headers` as a Headers
             INSTANCE, and spreading one yields {} — which silently dropped the
             `apikey` header Supabase requires and 401'd every request in the app.
             The Headers constructor accepts a Headers, a plain object, or pairs,
             so it preserves whatever supabase-js sent.

             Building a new one also means we never mutate supabase-js's own header
             object, which it reuses between requests — that would leak one
             request's token into the next. */
          const headers = new Headers(init.headers || {});
          if (token) headers.set('Authorization', `Bearer ${token}`);
          return fetch(input, { ...init, headers });
        },
      },
    },
  );
  return client;
}

/** Test seam. The app never calls this — the client is process-lifetime. */
export function resetSupabaseClient() {
  client = null;
  getToken = async () => null;
}

/** Test seam: stand in for a Clerk session without mounting a component. */
export function setSupabaseTokenGetter(fn) {
  getToken = fn;
}

export function useSupabase() {
  const { session } = useSession();
  /* A ref, not a dependency: `session` is a new object on many Clerk renders, so
     closing over it directly would mean re-registering constantly, and callers
     that hold the returned function in a ref (recordStore) would pin an old one. */
  const sessionRef = useRef(session);
  sessionRef.current = session;

  return async () => {
    getToken = () => sessionRef.current?.getToken({ template: 'supabase' }) ?? null;
    return getSupabaseClient();
  };
}
